<script setup lang="ts">
import { LinkItem, NuxtLinkItem } from '../shared'
import ElLogo from '../logo-themable/Logo.component.vue'
import SocialLinks from './children/SocialLinks.component.vue'
import CompanyAllRightsReserved from '../company-copyright/AllRightReserved.component.vue'
import { config } from '@/config'
import { useLocalization } from '@/pages-hooks'

//const homeData = <any>useAttrs().homeData
interface TProps {
  lcid: string
  homeData: any
}
const { lcid, homeData } = defineProps<TProps>()
const { translate, getLocalizedPathPrefix } = useLocalization()
const analytics = useAnalytics()

const ulCss = `mt-2 flex flex-col gap-2`
const linkItemCss = `flex gap-2 items-center text-sm leading-6 content-body opacity-85 hover:opacity-100  hover:text-primary`
const linkItemIconCss = `h-4 w-auto flex-none`

const onFooterItemClicked = (key: string) => {
  analytics.track('click', `footer-menu_${key}`)
}
</script>

<template>
  <footer id="footer" role="contentinfo" aria-labelledby="footer-heading" class="pb-4">
    <h2 id="footer-heading" class="sr-only">Footer</h2>
    <div class="mx-auto max-w-7xl px-6 pb-8 pt-16 sm:pt-24 lg:px-8 lg:pt-32">
      <div class="lg:grid lg:grid-cols-3 lg:gap-8">
        <!-- begin: company logo wrapper -->
        <div class="flex flex-col gap-y-4">
          <a :href="`${getLocalizedPathPrefix(lcid)}/`">
            <span class="sr-only">Home</span>
            <ElLogo cssClass="max-w-48 shrink-0" :brandConfig="config.brand" />
          </a>

          <p v-if="homeData?.hero?.brandMotto" class="content-body opacity-70 text-sm leading-6">
            {{ homeData?.hero?.brandMotto }}
          </p>
          <div class="flex w-full mt-4 sm:mt-8">
            <SocialLinks />
          </div>
        </div>
        <!-- end: company logo wrapper -->

        <!-- begin: company links columns -->
        <div
          class="w-full col-span-2 flex flex-col content-evenly gap-y-8 mt-8 md:flex-row md:gap-x-8 sm:mt-14"
        >
          <div class="flex-1">
            <h3 class="content-body text-sm font-semibold leading-6">
              {{ translate('footer.downloadsSection') }}
            </h3>
            <ul role="list" class="mt-2 space-y-1">
              <li>
                <NuxtLinkItem
                  :to="`${getLocalizedPathPrefix(lcid)}/releases#top`"
                  rel="noopener"
                  :overrideCssClass="linkItemCss"
                  @clicked="onFooterItemClicked('releases-react')"
                  >Release History</NuxtLinkItem
                >
              </li>
              <li>
                <LinkItem
                  to="https://www.npmjs.com/package/@tenorlab/react-dashboard"
                  target="_blank"
                  :overrideCssClass="linkItemCss"
                  @clicked="onFooterItemClicked('releases-react')"
                >
                  React Core (open source)
                </LinkItem>
              </li>
              <li>
                <LinkItem
                  to="https://www.npmjs.com/package/@tenorlab/vue-dashboard"
                  target="_blank"
                  :overrideCssClass="linkItemCss"
                  @clicked="onFooterItemClicked('releases-vue')"
                >
                  Vue Core (open source)
                </LinkItem>
              </li>
            </ul>
          </div>

          <div class="flex-1">
            <h3 class="content-body text-sm font-semibold leading-6">
              {{ homeData?.footer?.demos?.title }}
            </h3>
            <ul role="list" :class="ulCss">
              <li v-for="demolink in homeData?.footer?.demos?.links" :key="demolink.id">
                <LinkItem
                  :to="demolink.url"
                  rel="noopener"
                  target="_blank"
                  :title="demolink.title"
                  :overrideCssClass="linkItemCss"
                  @clicked="onFooterItemClicked(demolink.id)"
                >
                  <img :class="linkItemIconCss" :src="demolink.icon" :alt="demolink.iconAlt" height="16" />
                  <span>{{ demolink.name }}</span>
                </LinkItem>
              </li>
            </ul>
          </div>

          <!-- <div class="flex-1">
            <h3 class="content-body text-sm font-semibold leading-6">
              {{ translate('footer.contactUs') }}
            </h3>
            <ul role="list" :class="ulCss">
              <li>
                <NuxtLinkItem
                  :to="`${getLocalizedPathPrefix(lcid)}/contact-us`"
                  rel="noopener"
                  :overrideCssClass="linkItemCss"
                  @clicked="onFooterItemClicked('contact-us')"
                  >{{ translate('footer.contactUs') }}</NuxtLinkItem
                >
              </li>
            </ul>
          </div> -->

          <div class="flex-1">
            <h3 class="content-body text-sm font-semibold leading-6">
              {{ translate('footer.buySection') }}
            </h3>
            <ul role="list" class="mt-2 space-y-1">
              <li>
                <LinkItem
                  :to="`${getLocalizedPathPrefix(lcid)}/pricing#top`"
                  rel="noopener"
                  :overrideCssClass="linkItemCss"
                  @clicked="onFooterItemClicked('pricing')"
                >
                  {{ translate('footer.pricing') }}
                </LinkItem>
              </li>
            </ul>
          </div>

          <div class="flex-1">
            <h3 class="content-body text-sm font-semibold leading-6">
              {{ translate('footer.companySection') }}
            </h3>
            <ul role="list" class="mt-2 space-y-1">
              <li>
                <NuxtLinkItem
                  :to="`${getLocalizedPathPrefix(lcid)}/terms-of-use#top`"
                  rel="noopener"
                  :overrideCssClass="linkItemCss"
                  @clicked="onFooterItemClicked('terms-of-use')"
                  >{{ translate('footer.terms') }}</NuxtLinkItem
                >
              </li>
              <li>
                <NuxtLinkItem
                  :to="`${getLocalizedPathPrefix(lcid)}/privacy-policy#top`"
                  rel="noopener"
                  :overrideCssClass="linkItemCss"
                  @clicked="onFooterItemClicked('privacy-policy')"
                  >{{ translate('footer.privacy') }}</NuxtLinkItem
                >
              </li>
            </ul>
          </div>
        </div>
        <!-- end: company links columns -->
      </div>

      <div
        class="flex flex-col mt-4 pt-4 border-t border-white/10 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex sm:order-1 space-x-6 flex-nowrap whitespace-nowrap">
          <CompanyAllRightsReserved :text="homeData?.copyright" />
        </div>
      </div>
    </div>
  </footer>
</template>
